// hooks/useCookieUtils.ts
import { useCookieConsent } from '@contexts/CookieConsentContext';

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS;

interface AnalyticsEvent {
  [key: string]: any;
}

interface MarketingData {
  [key: string]: any;
}

// Hook for analytics tracking
export const useAnalytics = () => {
  const { canUse, isLoaded } = useCookieConsent();
  const canUseAnalytics = canUse('analytics');
  
  const trackEvent = (eventName: string, properties: AnalyticsEvent = {}): void => {
    // Google Analytics 4 tracking
    if (canUseAnalytics && typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', eventName, properties);
    }

    void import('@src/services/posthog/browser').then(({ captureEvent }) => {
      captureEvent(eventName, properties);
    });
  };
  
  const trackPageView = (page: string, properties: AnalyticsEvent = {}): void => {
    if (canUseAnalytics && typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', GA_MEASUREMENT_ID, {
        page_path: page,
        ...properties,
      });
    }

    void import('@src/services/posthog/browser').then(({ capturePageView }) => {
      capturePageView(page, properties);
    });
    void import('@src/services/openaiAds/browser').then(({ trackOpenAIAdsPageView }) => {
      trackOpenAIAdsPageView(page);
    });
  };
  
  return { 
    trackEvent, 
    trackPageView, 
    canTrack: canUseAnalytics,
    isLoaded,
  };
};

// Hook for marketing pixels
export const useMarketing = () => {
  const { canUse } = useCookieConsent();
  
  const trackConversion = (eventName: string, data: MarketingData = {}): void => {
    if (!canUse('marketing')) {
      console.log('Marketing tracking blocked by user consent');
      return;
    }
    
    // Facebook Pixel tracking
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', eventName, data);
    }
  };
  
  return { 
    trackConversion, 
    canTrack: canUse('marketing') 
  };
};

// Hook for functional cookies (preferences, etc.)
export const useFunctionalCookies = () => {
  const { canUse } = useCookieConsent();
  
  const setPreference = <T>(key: string, value: T): boolean => {
    if (!canUse('functional')) {
      console.log('Functional cookies blocked by user consent');
      return false;
    }
    
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(`pref_${key}`, JSON.stringify(value));
        return true;
      } catch (e) {
        console.error('Failed to save preference:', e);
        return false;
      }
    }
    return false;
  };
  
  const getPreference = <T>(key: string, defaultValue: T): T => {
    if (!canUse('functional') || typeof window === 'undefined') {
      return defaultValue;
    }
    
    try {
      const item = localStorage.getItem(`pref_${key}`);
      return item ? JSON.parse(item) : defaultValue;
    } catch (e) {
      console.error('Failed to get preference:', e);
      return defaultValue;
    }
  };
  
  return { 
    setPreference, 
    getPreference, 
    canUseFunctional: canUse('functional') 
  };
};
